import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Save, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  SavedAGB,
  BillingModel,
  agbProvider,
  defaultAGBCustomer,
  defaultAGBServiceScope,
  defaultAGBCommercialTerms,
  defaultAGBContractTerms,
  getIncompleteAGBFields,
} from '@/lib/constants/agbTemplateData';
import { v4 as uuidv4 } from 'uuid';

interface AGBFormProps {
  existingAGB: SavedAGB | null;
  onSave: (agb: SavedAGB) => void;
  onCancel: () => void;
}

interface FormData {
  customerName: string;
  customerAddress: string;
  customerRepresentedBy: string;
  seats: number;
  apiAccess: boolean;
  apiRateLimit: string;
  creditsPerMonth: string;
  pricing: string;
  paymentTermDays: number;
  pricePerUser: string;
  fixedFee: string;
  creditsIncluded: string;
  cooperationCostBasis: string;
  startDate: string;
  duration: string;
  noticePeriod: string;
  slaUptime: string;
  liabilityCap: string;
  mainContractReference: string;
  fixedEndDate: string;
}

const AGBForm: React.FC<AGBFormProps> = ({ existingAGB, onSave, onCancel }) => {
  const [incompleteFields, setIncompleteFields] = useState<string[]>(() =>
    existingAGB ? getIncompleteAGBFields(existingAGB) : []
  );

  // State for select/switch fields not handled by react-hook-form
  const [apiAccess, setApiAccess] = useState(existingAGB?.serviceScope.apiAccess ?? defaultAGBServiceScope.apiAccess);
  const [billingModel, setBillingModel] = useState<BillingModel>(existingAGB?.commercialTerms.billingModel ?? defaultAGBCommercialTerms.billingModel);
  const [supportLevel, setSupportLevel] = useState<'email' | 'phone' | 'dedicated'>(existingAGB?.contractTerms.supportLevel ?? defaultAGBContractTerms.supportLevel);
  const [liabilityCapType, setLiabilityCapType] = useState<'annual_value' | 'fixed'>(existingAGB?.contractTerms.liabilityCapType ?? defaultAGBContractTerms.liabilityCapType);
  const [ndaLevel, setNdaLevel] = useState<'standard' | 'extended'>(existingAGB?.contractTerms.ndaLevel ?? defaultAGBContractTerms.ndaLevel);
  const [autoRenewal, setAutoRenewal] = useState(existingAGB?.contractTerms.autoRenewal ?? defaultAGBContractTerms.autoRenewal);
  const [tenantIsolation, setTenantIsolation] = useState(existingAGB?.contractTerms.tenantIsolation ?? defaultAGBContractTerms.tenantIsolation);
  const [customReviewCriteria, setCustomReviewCriteria] = useState(existingAGB?.contractTerms.customReviewCriteria ?? defaultAGBContractTerms.customReviewCriteria);
  const [customCriteriaIP, setCustomCriteriaIP] = useState<'client_owned' | 'shared' | 'paperverse_owned'>(existingAGB?.contractTerms.customCriteriaIP ?? defaultAGBContractTerms.customCriteriaIP);
  const [activeConsentForChanges, setActiveConsentForChanges] = useState(existingAGB?.contractTerms.activeConsentForChanges ?? defaultAGBContractTerms.activeConsentForChanges);

  const defaults: FormData = existingAGB
    ? {
        customerName: existingAGB.customer.name,
        customerAddress: existingAGB.customer.address,
        customerRepresentedBy: existingAGB.customer.representedBy,
        seats: existingAGB.serviceScope.seats,
        apiAccess: existingAGB.serviceScope.apiAccess,
        apiRateLimit: existingAGB.serviceScope.apiRateLimit,
        creditsPerMonth: existingAGB.serviceScope.creditsPerMonth,
        pricing: existingAGB.commercialTerms.pricing,
        paymentTermDays: existingAGB.commercialTerms.paymentTermDays,
        pricePerUser: existingAGB.commercialTerms.pricePerUser,
        fixedFee: existingAGB.commercialTerms.fixedFee,
        creditsIncluded: existingAGB.commercialTerms.creditsIncluded,
        cooperationCostBasis: existingAGB.commercialTerms.cooperationCostBasis,
        startDate: existingAGB.contractTerms.startDate,
        duration: existingAGB.contractTerms.duration,
        noticePeriod: existingAGB.contractTerms.noticePeriod,
        slaUptime: existingAGB.contractTerms.slaUptime,
        liabilityCap: existingAGB.contractTerms.liabilityCap,
        mainContractReference: existingAGB.contractTerms.mainContractReference,
        fixedEndDate: existingAGB.contractTerms.fixedEndDate,
      }
    : {
        customerName: defaultAGBCustomer.name,
        customerAddress: defaultAGBCustomer.address,
        customerRepresentedBy: defaultAGBCustomer.representedBy,
        seats: defaultAGBServiceScope.seats,
        apiAccess: defaultAGBServiceScope.apiAccess,
        apiRateLimit: defaultAGBServiceScope.apiRateLimit,
        creditsPerMonth: defaultAGBServiceScope.creditsPerMonth,
        pricing: defaultAGBCommercialTerms.pricing,
        paymentTermDays: defaultAGBCommercialTerms.paymentTermDays,
        pricePerUser: defaultAGBCommercialTerms.pricePerUser,
        fixedFee: defaultAGBCommercialTerms.fixedFee,
        creditsIncluded: defaultAGBCommercialTerms.creditsIncluded,
        cooperationCostBasis: defaultAGBCommercialTerms.cooperationCostBasis,
        startDate: defaultAGBContractTerms.startDate,
        duration: defaultAGBContractTerms.duration,
        noticePeriod: defaultAGBContractTerms.noticePeriod,
        slaUptime: defaultAGBContractTerms.slaUptime,
        liabilityCap: defaultAGBContractTerms.liabilityCap,
        mainContractReference: defaultAGBContractTerms.mainContractReference,
        fixedEndDate: defaultAGBContractTerms.fixedEndDate,
      };

  const { register, handleSubmit } = useForm<FormData>({ defaultValues: defaults });

  const buildAGB = (data: FormData): SavedAGB => {
    const now = new Date().toISOString();
    return {
      id: existingAGB?.id || uuidv4(),
      customer: {
        name: data.customerName,
        address: data.customerAddress,
        representedBy: data.customerRepresentedBy,
      },
      serviceScope: {
        seats: Number(data.seats),
        apiAccess,
        apiRateLimit: data.apiRateLimit,
        creditsPerMonth: data.creditsPerMonth,
        customFeatures: existingAGB?.serviceScope.customFeatures || [],
      },
      commercialTerms: {
        pricing: data.pricing,
        paymentTermDays: Number(data.paymentTermDays),
        billingModel,
        pricePerUser: data.pricePerUser,
        fixedFee: data.fixedFee,
        creditsIncluded: data.creditsIncluded,
        cooperationCostBasis: data.cooperationCostBasis,
      },
      contractTerms: {
        startDate: data.startDate,
        duration: data.duration,
        noticePeriod: data.noticePeriod,
        slaUptime: data.slaUptime,
        supportLevel,
        liabilityCapType,
        liabilityCap: data.liabilityCap,
        ndaLevel,
        mainContractReference: data.mainContractReference,
        autoRenewal,
        fixedEndDate: data.fixedEndDate,
        tenantIsolation,
        customReviewCriteria,
        customCriteriaIP,
        activeConsentForChanges,
      },
      createdAt: existingAGB?.createdAt || now,
      updatedAt: now,
      status: existingAGB?.status || 'draft',
    };
  };

  const onSubmit = (data: FormData) => {
    const agb = buildAGB(data);
    setIncompleteFields(getIncompleteAGBFields(agb));
    onSave(agb);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Zurück
        </Button>
        <h1 className="text-2xl font-bold">
          {existingAGB ? 'AGB bearbeiten' : 'Neue AGB erstellen'}
        </h1>
      </div>

      {incompleteFields.length > 0 && (
        <Alert variant="destructive" className="border-yellow-500 bg-yellow-50 text-yellow-900 dark:bg-yellow-950 dark:text-yellow-200 dark:border-yellow-700 [&>svg]:text-yellow-600">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Unvollständiger Entwurf</AlertTitle>
          <AlertDescription>
            Folgende Angaben fehlen noch: {incompleteFields.join(', ')}.
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Kunde */}
        <Card>
          <CardHeader><CardTitle>Kunde (Auftraggeber)</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customerName">Organisation *</Label>
                <Input id="customerName" placeholder="z.B. Fraunhofer ENAS" {...register('customerName')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customerAddress">Anschrift *</Label>
                <Input id="customerAddress" placeholder="Straße, PLZ Ort" {...register('customerAddress')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customerRepresentedBy">Vertretungsberechtigte Person *</Label>
                <Input id="customerRepresentedBy" placeholder="z.B. Prof. Dr. X" {...register('customerRepresentedBy')} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Leistungsumfang */}
        <Card>
          <CardHeader><CardTitle>Leistungsumfang</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="seats">Anzahl Nutzerlizenzen (Seats)</Label>
                <Input id="seats" type="number" min={1} {...register('seats')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="creditsPerMonth">KI-Review-Credits / Monat</Label>
                <Input id="creditsPerMonth" {...register('creditsPerMonth')} />
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={apiAccess} onCheckedChange={setApiAccess} id="apiAccess" />
                <Label htmlFor="apiAccess">API-Zugang gewähren</Label>
              </div>
              {apiAccess && (
                <div className="space-y-2">
                  <Label htmlFor="apiRateLimit">API Rate-Limit</Label>
                  <Input id="apiRateLimit" placeholder="z.B. 1000 Requests/Stunde" {...register('apiRateLimit')} />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Kommerzielle Konditionen */}
        <Card>
          <CardHeader><CardTitle>Kommerzielle Konditionen</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Preismodell</Label>
                <Select value={billingModel} onValueChange={(v) => setBillingModel(v as BillingModel)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monatliche Subscription</SelectItem>
                    <SelectItem value="yearly">Jährliche Subscription</SelectItem>
                    <SelectItem value="one_time_per_user">Einmalzahlung pro Nutzer</SelectItem>
                    <SelectItem value="credits_plus_fixed">Credits + Fixbetrag</SelectItem>
                    <SelectItem value="cooperation">Kooperationsmodell (nur techn. Kosten)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {billingModel !== 'cooperation' && (
                <div className="space-y-2">
                  <Label htmlFor="pricing">Vergütung (netto) *</Label>
                  <Input id="pricing" placeholder="z.B. 1.200 EUR/Jahr" {...register('pricing')} />
                </div>
              )}

              {billingModel === 'one_time_per_user' && (
                <div className="space-y-2">
                  <Label htmlFor="pricePerUser">Preis pro Nutzer (einmalig, netto)</Label>
                  <Input id="pricePerUser" placeholder="z.B. 10 EUR" {...register('pricePerUser')} />
                </div>
              )}

              {billingModel === 'credits_plus_fixed' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="fixedFee">Fixbetrag (netto)</Label>
                    <Input id="fixedFee" placeholder="z.B. 500 EUR/Monat" {...register('fixedFee')} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="creditsIncluded">Inkludierte Credits</Label>
                    <Input id="creditsIncluded" placeholder="z.B. 100 Credits/Monat" {...register('creditsIncluded')} />
                  </div>
                </>
              )}

              {billingModel === 'cooperation' && (
                <div className="space-y-2">
                  <Label htmlFor="cooperationCostBasis">Kostenbasis (technol. Kosten)</Label>
                  <Input id="cooperationCostBasis" placeholder="z.B. API-Kosten + Hosting anteilig" {...register('cooperationCostBasis')} />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="paymentTermDays">Zahlungsziel (Tage)</Label>
                <Input id="paymentTermDays" type="number" min={7} {...register('paymentTermDays')} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vertragskonditionen */}
        <Card>
          <CardHeader><CardTitle>Vertragskonditionen</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">Vertragsbeginn *</Label>
                <Input id="startDate" type="date" {...register('startDate')} />
              </div>
              {autoRenewal && (
                <div className="space-y-2">
                  <Label htmlFor="duration">Laufzeit</Label>
                  <Input id="duration" placeholder="z.B. 12 Monate" {...register('duration')} />
                </div>
              )}

              <div className="flex items-center gap-3">
                <Switch checked={autoRenewal} onCheckedChange={setAutoRenewal} id="autoRenewal" />
                <Label htmlFor="autoRenewal">Automatische Verlängerung</Label>
              </div>

              {autoRenewal ? (
                <div className="space-y-2">
                  <Label htmlFor="noticePeriod">Kündigungsfrist</Label>
                  <Input id="noticePeriod" placeholder="z.B. 3 Monate zum Quartalsende" {...register('noticePeriod')} />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="fixedEndDate">Festes Vertragsende *</Label>
                  <Input id="fixedEndDate" type="date" {...register('fixedEndDate')} />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="slaUptime">SLA-Verfügbarkeit</Label>
                <Input id="slaUptime" placeholder="z.B. 99,5%" {...register('slaUptime')} />
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Support-Level</Label>
                <Select value={supportLevel} onValueChange={(v) => setSupportLevel(v as 'email' | 'phone' | 'dedicated')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">E-Mail-Support</SelectItem>
                    <SelectItem value="phone">Telefon-Support</SelectItem>
                    <SelectItem value="dedicated">Dedizierter Ansprechpartner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Haftungsobergrenze</Label>
                <Select value={liabilityCapType} onValueChange={(v) => setLiabilityCapType(v as 'annual_value' | 'fixed')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="annual_value">Jährlicher Vertragswert</SelectItem>
                    <SelectItem value="fixed">Fester Betrag</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {liabilityCapType === 'fixed' && (
                <div className="space-y-2">
                  <Label htmlFor="liabilityCap">Haftungsbetrag</Label>
                  <Input id="liabilityCap" placeholder="z.B. 50.000 EUR" {...register('liabilityCap')} />
                </div>
              )}
              <div className="space-y-2">
                <Label>Geheimhaltungsstandard</Label>
                <Select value={ndaLevel} onValueChange={(v) => setNdaLevel(v as 'standard' | 'extended')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="extended">Erweitert (unveröffentlichte Forschungsdaten)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            {/* Institutional / ENAS toggles */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Institutionelle Optionen</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <Switch checked={tenantIsolation} onCheckedChange={setTenantIsolation} id="tenantIsolation" />
                  <div>
                    <Label htmlFor="tenantIsolation">Mandantentrennung (eigener Bereich)</Label>
                    <p className="text-xs text-muted-foreground">Privates Dashboard, logische Datenisolierung</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Switch checked={customReviewCriteria} onCheckedChange={setCustomReviewCriteria} id="customReviewCriteria" />
                  <div>
                    <Label htmlFor="customReviewCriteria">Individuelle Review-Kriterien</Label>
                    <p className="text-xs text-muted-foreground">Gemeinsam entwickelte Bewertungsstandards</p>
                  </div>
                </div>

                {customReviewCriteria && (
                  <div className="space-y-2">
                    <Label>IP-Zuordnung der Custom-Kriterien</Label>
                    <Select value={customCriteriaIP} onValueChange={(v) => setCustomCriteriaIP(v as 'client_owned' | 'shared' | 'paperverse_owned')}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="client_owned">Kunde behält alle Rechte</SelectItem>
                        <SelectItem value="shared">Gemeinsames Eigentum</SelectItem>
                        <SelectItem value="paperverse_owned">PaperVerse behält alle Rechte</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <Switch checked={activeConsentForChanges} onCheckedChange={setActiveConsentForChanges} id="activeConsentForChanges" />
                  <div>
                    <Label htmlFor="activeConsentForChanges">Aktive Zustimmung bei AGB-Änderungen</Label>
                    <p className="text-xs text-muted-foreground">Für öffentliche Auftraggeber — kein „Schweigen = Zustimmung"</p>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="mainContractReference">Verweis auf Hauptvertrag</Label>
              <Input
                id="mainContractReference"
                placeholder="z.B. Kooperationsvertrag vom 01.03.2025 zwischen Fraunhofer ENAS und PaperVerse UG"
                {...register('mainContractReference')}
              />
              <p className="text-xs text-muted-foreground">
                Optional. Wird in § 1 Abs. 3 als Hauptvertrag referenziert.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Anbieter (read-only) */}
        <Card>
          <CardHeader><CardTitle>Anbieter (Auftragnehmer)</CardTitle></CardHeader>
          <CardContent>
            <div className="bg-muted/50 rounded-lg p-4 space-y-1 text-sm">
              <p><strong>{agbProvider.name}</strong></p>
              <p>{agbProvider.address}</p>
              <p>Vertreten durch: {agbProvider.representedBy}</p>
              <p>E-Mail: {agbProvider.email}</p>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Diese Daten werden in der Konfiguration verwaltet und sind hier nicht editierbar.
            </p>
          </CardContent>
        </Card>

        <div className="flex gap-4 justify-end">
          <Button type="button" variant="outline" onClick={onCancel}>
            Abbrechen
          </Button>
          <Button type="submit">
            <Save className="h-4 w-4 mr-2" />
            {existingAGB ? 'Änderungen speichern' : 'AGB erstellen'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AGBForm;
